import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PanelHeader } from '@frontend/panel-engine';
import { theme } from '@frontend/theme';
import { Button, Chip, Tabs, Text, usePopoverDialog } from '@frontend/design-system';
import { TaskFilters } from '../components';
import { TaskCenterTabs } from '../constants';
import { useTaskCenterStore } from '../provider';
import { taskCenterTrackingIds } from '../tracking-ids';
import { getAssigneeDetails } from '../utils';
import { TaskAssigneeFilter } from './filters/task-assignee-filter/task-assignee-filters';
import { TaskByStatus } from './task-by-status';

export const TaskList = () => {
  const { t } = useTranslation('task-center');
  const { taskMode, setTaskMode, setTab, selectedTab, assignees, assigneeFilter, setAssigneeFilter } =
    useTaskCenterStore([
      'taskMode',
      'setTaskMode',
      'setTab',
      'selectedTab',
      'assignees',
      'assigneeFilter',
      'setAssigneeFilter',
    ]);

  const { name: assignee, AvatarComponent } = getAssigneeDetails(assignees, assigneeFilter);
  const popoverDialogProps = usePopoverDialog();

  const openCreateTask = () => setTaskMode('create');

  return (
    // Adding motion div for future animation collapsing rtl
    <motion.div
      initial={{ display: 'block' }}
      animate={{ display: taskMode ? 'none' : 'block' }}
      exit={{ display: 'none' }}
      transition={{ duration: 0 }}
      css={containerStyle}
    >
      <header css={headerStyle}>
        <PanelHeader.Title level={3}>{t('All Tasks')}</PanelHeader.Title>
        <span>
          <Text size='medium'>{t('Assigned To:')}</Text>
          <Chip.StatusSwitcher
            variant='neutral'
            // @ts-expect-error Right element prop does not exists in the chip, but is available as it extending Chip
            rightElement={<Icon name='caret-down-small' size={12} />}
            css={css`
              border: none;
              max-width: 100%;
            `}
            {...popoverDialogProps.getTriggerProps()}
          >
            {AvatarComponent && <AvatarComponent />}
            {assignee}
          </Chip.StatusSwitcher>
          {popoverDialogProps.isOpen && (
            <TaskAssigneeFilter
              setValue={(value: string) => {
                setAssigneeFilter(value);
                popoverDialogProps.close();
              }}
              selectedValue={assigneeFilter}
              getDialogProps={popoverDialogProps.getDialogProps}
            />
          )}
        </span>

        <span>
          <TaskFilters />
          <Button
            iconName='draft'
            variant='secondary'
            size='large'
            onClick={openCreateTask}
            trackingId={taskCenterTrackingIds.createTaskDraftButton}
            label={t('Create Task')}
            hoverLabel={t('Create Task')}
          />
        </span>
      </header>
      <Tabs initialTab={selectedTab} controlledTab={selectedTab} onChange={(id) => setTab(id)}>
        <Tabs.Bar>
          {TaskCenterTabs.map(({ title, controller, trackingId }) => (
            <Tabs.Tab
              id={controller}
              css={tabButtonStyle}
              key={`tab-${controller}`}
              trackingId={trackingId}
              aria-label={t('{{tab}} tab', { tab: title })}
            >
              {title}
            </Tabs.Tab>
          ))}
        </Tabs.Bar>
        {TaskCenterTabs.map(({ status, controller }) => (
          <Tabs.Panel controller={controller} key={`tab-panel-${controller}`}>
            <div css={taskListContainer}>
              <TaskByStatus status={status} />
            </div>
          </Tabs.Panel>
        ))}
      </Tabs>
    </motion.div>
  );
};

const containerStyle = css`
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const headerStyle = css`
  padding: ${theme.spacing(2)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${theme.colors.neutral10};
  border-bottom: 1px solid ${theme.colors.neutral10};
  span {
    display: flex;
    gap: ${theme.spacing(1)};
  }
`;

const tabButtonStyle = css`
  flex: 1;
`;

const taskListContainer = css`
  height: 100%;
`;
